import { MutationStatus } from '@tanstack/react-query';

import { toast } from '@/shared/components';
import { QUERY_KEY_INITIALS } from '@/shared/constants/query-key-initials';
import { getFormEditDetails, updateFormEditDetails } from '@/shared/services/forms/forms';
import {
	ISFormEditDetailsResponse,
	IUpdateFormEditDetailsPayload,
	TLinkedInExclusionReason,
} from '@/shared/services/forms/forms.types';

import { getQueryClient } from '@/utils/get-query-client';

export interface IFormSaveRequest {
	payload: IUpdateFormEditDetailsPayload;
	successText?: string;
	errorText?: string;
}

type TStatusListener = (status: MutationStatus) => void;

/**
 * Serializes `/form/edit` calls for one form. Each payload is a full snapshot of the
 * form, so only the newest queued one needs sending: at most one request is in flight,
 * and edits made meanwhile collapse into a single follow-up. This keeps an older
 * snapshot from landing after a newer one and reverting it.
 *
 * Queues live at module scope so a save still in flight when the editor unmounts
 * finishes, and the next visit's fetch can wait for it.
 */
function createFormSaveQueue(formId: string) {
	let pending: IFormSaveRequest | null = null;
	let draining: Promise<void> | null = null;
	let status: MutationStatus = 'idle';
	const listeners = new Set<TStatusListener>();

	const queue = {
		getStatus: () => status,

		enqueue(request: IFormSaveRequest) {
			// A superseded request's toast texts still describe an action the user took.
			pending = {
				payload: request.payload,
				successText: request.successText ?? pending?.successText,
				errorText: request.errorText ?? pending?.errorText,
			};
			if (!draining) draining = drain();
		},

		whenIdle: (): Promise<void> => draining ?? Promise.resolve(),

		subscribe(listener: TStatusListener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};

	const setStatus = (nextStatus: MutationStatus) => {
		status = nextStatus;
		listeners.forEach((listener) => listener(nextStatus));
	};

	async function drain() {
		setStatus('pending');
		let lastFailed = false;

		while (pending) {
			const request = pending;
			pending = null;
			try {
				const response = await updateFormEditDetails(request.payload);
				// Each save re-evaluates LinkedIn eligibility (e.g. a title just emptied).
				applyLinkedInExclusionReason(formId, response.linkedinExcludedReason);
				lastFailed = false;
				if (request.successText) toast({ variant: 'success', title: request.successText });
			} catch {
				lastFailed = true;
				if (request.errorText) toast({ variant: 'destructive', title: request.errorText });
			}
		}

		draining = null;
		setStatus(lastFailed ? 'error' : 'success');
		// Other views (e.g. form details) may have fetched while these saves were in flight.
		getQueryClient().invalidateQueries({ queryKey: [QUERY_KEY_INITIALS.GET_FORM_DETAILS] });
	}

	return queue;
}

type TFormSaveQueue = ReturnType<typeof createFormSaveQueue>;

const queues = new Map<string, TFormSaveQueue>();

export function getFormSaveQueue(formId: string) {
	let queue = queues.get(formId);
	if (!queue) {
		queue = createFormSaveQueue(formId);
		queues.set(formId, queue);
	}
	return queue;
}

/** Lets a fresh fetch of the form wait for saves still in flight from a previous visit. */
export function waitForPendingFormSaves(formId: string) {
	return queues.get(formId)?.whenIdle() ?? Promise.resolve();
}

/**
 * Copies the server's LinkedIn verdict into the editor's cache. Only that field: the cache holds
 * the user's latest edits, so a server snapshot must never replace it wholesale.
 */
export function applyLinkedInExclusionReason(
	formId: string,
	reason: TLinkedInExclusionReason | null | undefined
) {
	getQueryClient().setQueryData<ISFormEditDetailsResponse>(
		[QUERY_KEY_INITIALS.GET_FORM_EDIT_DETAILS, formId],
		(prev) => {
			if (!prev) return prev;
			const next = reason ?? null;
			if ((prev.linkedinExcludedReason ?? null) === next) return prev;
			return { ...prev, linkedinExcludedReason: next };
		}
	);
}

/**
 * Re-reads the LinkedIn verdict for an open editor after a change that does not go through
 * `/form/edit` (e.g. a status change). No-op when the editor is not open.
 */
export async function refreshLinkedInExclusionReason(formId: string) {
	const queryKey = [QUERY_KEY_INITIALS.GET_FORM_EDIT_DETAILS, formId];
	if (!getQueryClient().getQueryData(queryKey)) return;

	await waitForPendingFormSaves(formId);
	try {
		const fresh = await getFormEditDetails({ id: formId });
		// A save that started meanwhile carries a newer verdict; let it win.
		if (getFormSaveQueue(formId).getStatus() === 'pending') return;
		applyLinkedInExclusionReason(formId, fresh.linkedinExcludedReason);
	} catch {
		// The next save brings the verdict anyway.
	}
}
